import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Loading from "../../Common/Loading";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface DeliveryDocketDetails {
  DistributorName: string;
  OrderDate: string;
  OrderRef: string;
  Name: string;
  Phone: string;
  Address: string;
  DisplayItems: string;
  ordercomments: string;
  Currency: string;
  Country: string;
}

const EL_Print_DeliveryDocketDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [details, setDetails] = useState<DeliveryDocketDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // Get query parameters
  const searchParams = new URLSearchParams(location.search);
  const ddCode = searchParams.get('DDCode') || '';
  const schoolCode = searchParams.get('SchoolCode') || '';
  const familyCode = searchParams.get('FamilyCode') || '';
  const login = searchParams.get('Login') || '';

  // Set background color
  useEffect(() => {
    document.body.style.backgroundColor = 'white';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
    };
  }, []);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!ddCode || !schoolCode || !familyCode || !login) {
        setErrorMsg('Missing required parameters');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          DDCode: ddCode,
          SchoolCode: schoolCode,
          FamilyCode: familyCode,
          Login: login
        });

        const response = await fetch(`${API_BASE_URL}api/delivery_docket_details/?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setDetails(data.data);
        } else {
          setErrorMsg(data.error || 'Failed to load delivery docket details');
        }
      } catch (error) {
        console.error('Error loading delivery docket details:', error);
        setErrorMsg('Failed to load delivery docket details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [ddCode, schoolCode, familyCode, login]);

  // Handle print (exactly as in VB)
  const handlePrint = () => {
    const printButton = document.getElementById('btnPrint');
    const closeButton = document.getElementById('btnClose');

    if (printButton) printButton.style.display = 'none';
    if (closeButton) closeButton.style.display = 'none';

    window.print();

    if (printButton) printButton.style.display = 'inline-block';
    if (closeButton) closeButton.style.display = 'inline-block';
  };

  // Handle close (exactly as in VB)
  const handleClose = () => {
    window.close();
  };

  // Show error message if present
  useEffect(() => {
    if (errorMsg) {
      alert(errorMsg);
    }
  }, [errorMsg]);

  // Set window size and position (exactly as in VB)
  useEffect(() => {
    const setWindowSize = () => {
      if (window.screen) {
        const width = window.screen.availWidth;
        const height = window.screen.availHeight;
        window.moveTo(0, 0);
        window.resizeTo(width, height);
      }
    };

    // Set initial size
    setWindowSize();

    // Add resize handler
    window.addEventListener('resize', setWindowSize);
    return () => window.removeEventListener('resize', setWindowSize);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (!details) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No delivery docket details found.</p>
        <button onClick={handleClose}>Close</button>
      </div>
    );
  }

  return (
    <div id="divItems" style={{ width: '100%' }}>
      {/* Header Table (exactly as in VB) */}
      <table id="tblHeader" cellPadding="5" cellSpacing="0" border={0} style={{ width: '100%' }}>
        <tbody>
          <tr>
            <td align="center">
              <span style={{
                fontFamily: 'Arial',
                fontSize: '12pt',
                fontWeight: 'bold',
                color: 'red'
              }}>
                <b>{details.DistributorName}</b>
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <br />

      {/* Details Table (exactly as in VB) */}
      <table id="tblDetails" cellPadding="5" cellSpacing="0" border={0} style={{
        width: '100%',
        fontFamily: 'Arial',
        fontSize: '10pt'
      }}>
        <tbody>
          <tr>
            <td style={{ width: '20%' }}>Order Date:</td>
            <td style={{ width: '80%' }}>{details.OrderDate}</td>
          </tr>
          <tr>
            <td>Order Ref.:</td>
            <td>{details.OrderRef}</td>
          </tr>
          <tr>
            <td colSpan={2}>&nbsp;</td>
          </tr>
          <tr>
            <td>Name:</td>
            <td>
              <span style={{ color: '#000066' }}>{details.Name}</span>
            </td>
          </tr>
          <tr>
            <td>Phone:</td>
            <td>
              <span style={{ color: '#000066' }}>{details.Phone}</span>
            </td>
          </tr>
          <tr>
            <td style={{ verticalAlign: 'top' }}>Address:</td>
            <td>
              <span style={{ color: '#000066' }}
                dangerouslySetInnerHTML={{ __html: details.Address }} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Items Table (exactly as in VB) */}
      <table cellPadding="5" cellSpacing="0" border={0} width="100%">
        <tbody>
          <tr>
            <td>
              <div dangerouslySetInnerHTML={{ __html: details.DisplayItems }} />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Order Comments and Buttons (exactly as in VB) */}
      <table cellPadding="5" cellSpacing="0" border={0} width="100%" style={{
        fontFamily: 'Arial',
        fontSize: '10pt'
      }}>
        <tbody>
          <tr>
            <td>
              <b>Order Comments:</b>&nbsp;
              {details.ordercomments}
            </td>
          </tr>
          <tr>
            <td>&nbsp;</td>
          </tr>
          <tr>
            <td id="tdPrint" align="center">
              <input
                id="btnPrint"
                type="button"
                value="Print"
                onClick={handlePrint}
                style={{
                  padding: '5px 15px',
                  marginRight: '20px',
                  cursor: 'pointer'
                }}
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
              <input
                id="btnClose"
                type="button"
                value="Close"
                onClick={handleClose}
                style={{
                  padding: '5px 15px',
                  cursor: 'pointer'
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {/* Hidden field for currency (exactly as in VB) */}
      <input type="hidden" id="hidCurrency" value={details.Currency} />
    </div>
  );
};

export default EL_Print_DeliveryDocketDetails;